<template>
  <div class="duty-inspect">
    <van-nav-bar title="巡课管理" fixed placeholder />

    <!-- 巡课签到（att_status：1=签到，2=取消；今天及以前可操作，未来不可） -->
    <div class="sign-bar">
      <span class="sign-tip">巡课签到</span>
      <div class="sign-actions">
        <van-button
          v-if="currentDate <= todayStr"
          size="small"
          type="primary"
          :loading="signing"
          :disabled="signedToday"
          :icon="signedToday ? 'passed' : ''"
          @click="signIn"
          >{{ signedToday ? "已签到" : "巡课签到" }}</van-button
        >
        <van-button
          v-if="currentDate <= todayStr && signedToday"
          size="small"
          plain
          type="danger"
          :loading="canceling"
          @click="cancelSign"
          >取消签到</van-button
        >
        <span v-else-if="currentDate > todayStr" class="sign-date-tip"
          >未来日期不可签到</span
        >
      </div>
    </div>

    <!-- 日期条：默认今天，可回看历史，不查未来 -->
    <div class="date-bar">
      <span
        class="date-nav"
        :class="{ disabled: !canPrev || signing || canceling || refreshing }"
        @click="prevDay"
        >‹</span
      >
      <span class="date-value">{{ currentDate }}</span>
      <span
        class="date-nav"
        :class="{ disabled: !canNext || signing || canceling || refreshing }"
        @click="nextDay"
        >›</span
      >
    </div>

    <!-- 全校课后服务班级列表 -->
    <div class="class-list">
      <div
        v-for="c in classList"
        :key="c.id + '-' + c.class_id"
        class="class-card"
        @click="openClass(c)"
      >
        <div class="class-headline">
          <span class="class-title">{{ c.title }}</span>
          <van-tag :type="courseTypeType(c.course_type)" plain size="medium">
            {{ courseTypeText(c.course_type) }}
          </van-tag>
        </div>
        <div class="class-name">{{ c.class_name }}</div>
        <div class="class-meta">
          <span v-if="c.location" class="meta-item">地点: {{ c.location }}</span>
        </div>
        <div v-if="c.service_time" class="class-time">时间: {{ c.service_time }}</div>
        <div class="class-count">
          <span
            >应到 <b>{{ c.student_num }}</b></span
          >
          <span
            >实到 <b>{{ c.attendance == 1 ? c.att_num : 0 }}</b></span
          >
          <span v-if="c.attendance == 1" class="duty-teacher"
            >签到老师 <b>{{ c.teacher_name }}</b></span
          >
          <span v-else class="duty-teacher duty-teacher--none">未签到</span>
        </div>
      </div>
      <van-empty v-if="classList.length === 0" description="当日暂无课后服务班级" />
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { Toast, Dialog, Button, NavBar, Empty, Tag } from "vant";
import api from "../service/api";
import { courseTypeText, courseTypeType } from "../utils/courseType";

export default {
  components: {
    [Button.name]: Button,
    [NavBar.name]: NavBar,
    [Empty.name]: Empty,
    [Tag.name]: Tag,
  },
  setup() {
    const router = useRouter();
    const currentDate = ref(formatDate(new Date()));
    const classList = ref([]);
    const signing = ref(false);
    // 取消签到独立 loading：避免取消时左侧"已签到"按钮跟着转圈
    const canceling = ref(false);
    // 今日是否已巡课签到（由后端查询接口判定）
    const signedToday = ref(false);
    // 正在向服务端核对签到状态：这期间不能翻日期，否则两次查询会互相覆盖
    const refreshing = ref(false);

    // 日期边界：不能晚于今天，不能早于 60 天前
    const todayStr = formatDate(new Date());
    const minDateStr = formatDate(addDays(new Date(), -60));
    const canNext = computed(() => currentDate.value < todayStr);
    const canPrev = computed(() => currentDate.value > minDateStr);

    // 切日期/操作后刷新签到状态：今天及以前调后端查询（历史日期可补签，需知已签状态），未来日期不查
    const refreshSignState = async () => {
      if (currentDate.value > todayStr) {
        signedToday.value = false;
        return;
      }
      refreshing.value = true;
      try {
        const res = await api.getDutyTeacherAttendance({
          sid: localStorage.getItem("sid"),
          teacher_id: sessionStorage.getItem("userId"),
          attendance_date: currentDate.value,
        });
        // 返回：{ code, result: { attendance_status } }，attendance_status=1 已签到 / 0 未签到
        signedToday.value =
          res.data.code == 1001 && res.data.result?.attendance_status == 1;
      } catch (e) {
        // 查询失败不动 signedToday：刚签到成功时被重置成"未签到"，按钮会变回可点
      } finally {
        refreshing.value = false;
      }
    };

    // 忙时拦截：请求中/核对状态中不允许翻日期，否则两次查询并发回来会互相覆盖状态
    const checkBusy = () => {
      if (signing.value || canceling.value || refreshing.value) {
        Toast("处理中，请稍候");
        return true;
      }
      return false;
    };

    const prevDay = () => {
      if (!canPrev.value) return;
      if (checkBusy()) return;
      currentDate.value = formatDate(addDays(parseDate(currentDate.value), -1));
      refreshSignState();
      load();
    };
    const nextDay = () => {
      if (!canNext.value) return;
      if (checkBusy()) return;
      currentDate.value = formatDate(addDays(parseDate(currentDate.value), 1));
      refreshSignState();
      load();
    };

    const load = async () => {
      try {
        Toast.loading({ message: "加载中...", forbidClick: true });
        const res = await api.getAllServiceClassListByDate({
          sid: localStorage.getItem("sid"),
          school_id: sessionStorage.getItem("tSchoolId"),
          search_date: currentDate.value,
        });
        Toast.clear();
        if (res.data.code == 1001) {
          classList.value = res.data.result || [];
        } else {
          Toast(res.data.message);
        }
      } catch (error) {
        Toast.clear();
        Toast("加载失败，请重试");
      }
    };

    // 巡课签到：att_status=1（签到）；今天及以前可操作，成功后重新请求确认状态
    const signIn = async () => {
      if (signedToday.value || currentDate.value > todayStr) return;
      // 当日全校无课后服务班级（含今天与历史回看日）→ 无需签到
      if (classList.value.length === 0) {
        Toast("当日无课后服务班级，无需签到");
        return;
      }
      signing.value = true;
      try {
        const res = await api.dutyTeacherAttendance({
          sid: localStorage.getItem("sid"),
          teacher_id: sessionStorage.getItem("userId"),
          attendance_date: currentDate.value,
          att_status: "1",
        });
        if (res.data.code == 1001) {
          // 乐观更新：按钮立刻切到"已签到"，不用等重查回来
          signedToday.value = true;
          Toast("巡课签到成功");
          await refreshSignState(); // 重新请求后端，以服务端状态为准
        } else {
          Toast(res.data.message || "巡课签到失败");
        }
      } catch (error) {
        Toast("巡课签到失败，请重试");
      } finally {
        signing.value = false;
      }
    };

    // 取消巡课签到：att_status=2（破坏性操作，需二次确认）
    const cancelSign = async () => {
      if (!signedToday.value || currentDate.value > todayStr) return;
      try {
        await Dialog.confirm({
          title: "取消签到",
          message: "确定取消该日巡课签到吗？",
        });
      } catch (e) {
        return; // 用户取消
      }
      canceling.value = true;
      try {
        const res = await api.dutyTeacherAttendance({
          sid: localStorage.getItem("sid"),
          teacher_id: sessionStorage.getItem("userId"),
          attendance_date: currentDate.value,
          att_status: "2",
        });
        if (res.data.code == 1001) {
          // 乐观更新：按钮立刻切回"巡课签到"
          signedToday.value = false;
          Toast("已取消签到");
          await refreshSignState(); // 重新请求后端，以服务端状态为准
        } else {
          Toast(res.data.message || "取消失败");
        }
      } catch (error) {
        Toast("取消失败，请重试");
      } finally {
        canceling.value = false;
      }
    };

    // 单点班级卡片 → 进入只读考勤详情（巡课不可修改）
    // 详情页接口 dutyGetServiceClassInfo 只需 serviceClassId(class_id) + date
    const openClass = (c) => {
      router.push({
        path: "/dutyClassAttend",
        query: {
          serviceClassId: c.class_id,
          date: currentDate.value,
          title: c.title,
        },
      });
    };

    onMounted(() => {
      refreshSignState();
      load();
    });

    return {
      currentDate,
      todayStr,
      classList,
      signing,
      canceling,
      signedToday,
      refreshing,
      canPrev,
      canNext,
      prevDay,
      nextDay,
      courseTypeText,
      courseTypeType,
      signIn,
      cancelSign,
      openClass,
    };
  },
};

function formatDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function parseDate(s) {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}
function addDays(d, n) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}
</script>

<style lang="less">
.duty-inspect {
  min-height: 100vh;
  background: #f7f8fa;

  .sign-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    background: #fff;
    border-bottom: 1px solid #f2f2f2;
  }
  .sign-tip {
    font-size: 14px;
    color: #333;
  }
  .sign-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .sign-date-tip {
    font-size: 12px;
    color: #999;
  }

  .date-bar {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 24px;
    padding: 14px 0;
    background: #fff;
  }
  .date-nav {
    font-size: 22px;
    color: #52c1b2;
    cursor: pointer;
  }
  .date-nav.disabled {
    color: #ccc;
    cursor: not-allowed;
  }
  .date-value {
    font-size: 16px;
    font-weight: 600;
    color: #333;
    min-width: 110px;
    text-align: center;
  }

  .class-list {
    // 底部预留 tabbar（50px + 安全区）高度，防止最后一张卡片被遮挡
    padding: 10px 12px calc(70px + env(safe-area-inset-bottom, 0px));
  }
  .class-card {
    background: #fff;
    border-radius: 10px;
    padding: 12px 14px;
    margin-bottom: 10px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
    cursor: pointer;
  }
  .class-headline {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .class-title {
    font-size: 15px;
    font-weight: 600;
    color: #333;
  }
  .class-name {
    margin-top: 4px;
    font-size: 13px;
    color: #666;
  }
  .class-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 6px;
    font-size: 12px;
    color: #999;
  }
  .class-time {
    margin-top: 4px;
    font-size: 12px;
    color: #999;
  }
  .class-count {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px dashed #eee;
    font-size: 13px;
    color: #555;
  }
  .class-count b {
    color: #333;
  }
  .duty-teacher--none {
    color: #bbb;
    font-size: 12px;
  }
}
</style>
